import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { payrollApi } from "../../services/payrollApi";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function MyPayrollPage() {
  const { t } = useLocalization();
  const [lines, setLines] = useState([]);
  const [disputingId, setDisputingId] = useState(null);
  const [disputeText, setDisputeText] = useState("");

  const load = () => {
    payrollApi.myLines().then((r) => setLines(r.data)).catch(() => setLines([]));
  };

  useEffect(() => { load(); }, []);

  const submitDispute = async (lineId) => {
    if (!disputeText.trim()) { toast.error(t("myPayroll.disputeEmptyError")); return; }
    try {
      await payrollApi.submitDispute(lineId, disputeText);
      toast.success(t("myPayroll.disputeSubmitted"));
      setDisputingId(null); setDisputeText("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="my-payroll-page" className="space-y-4">
      <PageHelpBanner pageKey="my-payroll" title={t("pageHelp.defaultTitle")}>
        <p>{t("pageHelp.myPayroll")}</p>
      </PageHelpBanner>
      <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("myPayroll.title")}</h2>
      {lines.length === 0 && <p className="text-sm text-gray-500" data-testid="no-payslips-message">{t("myPayroll.noPayslips")}</p>}
      {lines.map((l) => (
        <div key={l.id} className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3" data-testid={`my-payslip-card-${l.id}`}>
          <div className="flex items-center justify-between">
            <p className="font-semibold text-gray-900">{l.payroll_month}</p>
            <StatusBadge status={l.status} />
          </div>
          <dl className="text-sm space-y-1">
            <div className="flex justify-between"><dt className="text-gray-500">{t("myPayroll.regularHours")}</dt><dd>¥{l.regular_pay}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">{t("myPayroll.premiumPay")}</dt><dd>¥{l.premium_pay}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">{t("myPayroll.transportation")}</dt><dd>¥{l.transportation_allowance}</dd></div>
            <div className="flex justify-between font-bold border-t border-gray-100 pt-1"><dt>{t("myPayroll.grossPay")}</dt><dd data-testid={`gross-pay-${l.id}`}>¥{l.gross_pay}</dd></div>
          </dl>
          <div className="flex gap-2 flex-wrap">
            <a data-testid={`download-summary-${l.id}`} href={payrollApi.getMyLineReportUrl(l.id, "summary")} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("myPayroll.downloadSummary")}</a>
            <a data-testid={`download-detailed-${l.id}`} href={payrollApi.getMyLineReportUrl(l.id, "detailed")} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("myPayroll.downloadDetailed")}</a>
            {disputingId === l.id ? (
              <span className="flex items-center gap-2 w-full mt-2">
                <input data-testid={`dispute-text-input-${l.id}`} value={disputeText} onChange={(e) => setDisputeText(e.target.value)} placeholder={t("myPayroll.disputePlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm flex-1" />
                <button data-testid={`dispute-submit-btn-${l.id}`} onClick={() => submitDispute(l.id)} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600 text-white hover:bg-red-700">{t("myPayroll.disputeSubmit")}</button>
              </span>
            ) : (
              <button data-testid={`dispute-open-btn-${l.id}`} onClick={() => setDisputingId(l.id)} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50">{t("myPayroll.disputeOpen")}</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
